"use client";

import React from "react";
import { Calendar, User, Clock, ArrowRight, Share2, Tag } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { AspectRatio } from "@/components/ui/aspect-ratio";


function RelatedPostsConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div data-section-id="40912" className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {[1, 2, 3].map((_, i) => (
          <div key={i} data-index={i} className="h-80 animate-pulse rounded-md bg-muted" />
        ))}
      </div>
    );
  }

  if (error || !posts || posts.length === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
      {posts.map((post, i) => (
        <Card key={post.id} data-index={i} className="group overflow-hidden border border-border/50 bg-card shadow-sm transition-all hover:shadow-md">
          <div className="relative aspect-[4/3] overflow-hidden">
            {post.featuredImage ? (
              <img
                src={post.featuredImage}
                alt={post.title}
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-muted text-muted-foreground">
                <span className="text-sm font-sans">No Image</span>
              </div>
            )}
          </div>
          <CardHeader className="p-5 pb-2">
            <div className="mb-2 flex items-center gap-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">
              <Calendar className="h-3 w-3" />
              <span>{post.date ? new Date(post.date).toLocaleDateString() : "Recent Update"}</span>
            </div>
            <CardTitle className="line-clamp-2 text-lg font-bold leading-tight tracking-tight">
              <Link to={post.link || "#"} className="hover:text-primary transition-colors">
                {post.title}
              </Link>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-5 pt-2">
            <p className="line-clamp-3 text-sm leading-relaxed text-muted-foreground" dangerouslySetInnerHTML={{ __html: post.excerpt || "" }} />
          </CardContent>
          <CardFooter className="p-5 pt-0">
            <Link to={post.link || "#"} className="w-full">
              <Button variant="outline" className="w-full justify-between rounded-md border-border hover:border-primary/50 hover:text-primary transition-colors">
                Read Technical Guide <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
}

function RelatedPostsSection() {
  
  const q = { post_type: "post", per_page: 3 };
  const wp_query = new WP_Query(q);

  return (
    <div className="mt-16">
      <div className="mb-8 flex items-center justify-between">
        <h3 className="text-2xl font-bold tracking-tight text-foreground md:text-3xl">CNC Maintenance Insights</h3>
        <Link to="#">
          <Button variant="ghost" className="hidden font-medium text-muted-foreground hover:text-primary sm:flex">
            Browse All Guides <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </Link>
      </div>
      <PostsProvider wp_query={wp_query}>
        <RelatedPostsConsumer />
      </PostsProvider>
      <div className="mt-6 flex justify-center sm:hidden">
        <Link to="#">
          <Button variant="outline" className="rounded-md">View All Articles</Button>
        </Link>
      </div>
    </div>
  );
}


function SinglePostConsumer() {
  const { posts, loading, error, refetch } = usePosts();

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl space-y-8 p-6">
        <div className="h-12 w-3/4 animate-pulse rounded-md bg-muted" />
        <div className="aspect-video w-full animate-pulse rounded-md bg-muted" />
        <div className="space-y-4">
          <div className="h-4 w-full animate-pulse rounded bg-muted" />
          <div className="h-4 w-full animate-pulse rounded bg-muted" />
          <div className="h-4 w-2/3 animate-pulse rounded bg-muted" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center text-center">
        <h2 className="text-2xl font-bold text-destructive">Error Loading Technical Data</h2>
        <p className="text-muted-foreground">{error}</p>
        <Button onClick={() => refetch()} className="mt-4 rounded-md">
          Retry Connection
        </Button>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center text-center">
        <h2 className="text-2xl font-bold">Document Not Found</h2>
        <p className="text-muted-foreground">The technical article you are looking for does not exist in our database.</p>
        <Link to="/HomePage" className="mt-4">
          <Button className="rounded-md">Return Home</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];
  const authorName = post.author?.name || "CNCWise Technical Team";
  const authorAvatar = post.author?.avatar?.["96"] || "";
  const postDate = post.date ? new Date(post.date).toLocaleDateString("en-US", { year: 'numeric', month: 'long', day: 'numeric' }) : "";
  const category = post.categories?.[0]?.name || "CNC Maintenance";

  return (
    <article className="container mx-auto max-w-5xl px-4 py-12 md:py-20">
      {}
      <header className="mx-auto mb-10 max-w-3xl text-center">
        <div className="mb-6 flex items-center justify-center gap-2">
          <span className="rounded-md bg-primary/10 px-3 py-1 text-xs font-bold text-primary uppercase tracking-widest">
            {category}
          </span>
        </div>
        <h1 className="mb-6 text-3xl font-bold leading-tight tracking-tighter text-foreground md:text-4xl lg:text-5xl">
          {post.title}
        </h1>
        
        <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <Avatar className="h-8 w-8 border border-border/50">
              <AvatarImage src={authorAvatar} alt={authorName} />
              <AvatarFallback className="bg-muted"><User className="h-4 w-4 text-muted-foreground" /></AvatarFallback>
            </Avatar>
            <span className="font-semibold text-foreground">{authorName}</span>
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            <span>{postDate}</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            <span>6 min technical read</span>
          </div>
        </div>
      </header>

      {}
      <div className="mb-12 overflow-hidden rounded-md border border-border/50 shadow-md md:mb-16">
        <AspectRatio ratio={16 / 9}>
          {post.featuredImage ? (
            <img
              src={post.featuredImage}
              alt={post.title}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-muted">
              <span className="text-muted-foreground font-sans">Technical Illustration Unavailable</span>
            </div>
          )}
        </AspectRatio>
      </div>

      {}
      <div className="grid grid-cols-1 gap-12 lg:grid-cols-[1fr_300px]">
        {}
        <div className="min-w-0">
          <div 
            className="prose prose-lg prose-slate dark:prose-invert max-w-none
              prose-headings:font-sans prose-headings:font-bold prose-headings:tracking-tight
              prose-a:text-primary prose-a:no-underline hover:prose-a:underline
              prose-img:rounded-md prose-img:shadow-sm
              [&>p]:mb-6 [&>p]:leading-relaxed [&>p]:text-foreground/90 [&>h2]:mt-10 [&>h2]:mb-4 [&>h2]:text-2xl [&>h3]:mt-8 [&>h3]:mb-3 [&>h3]:text-xl [&>ul]:mb-6 [&>ul]:list-disc [&>ul]:pl-6 [&>li]:mb-2"
            dangerouslySetInnerHTML={{ __html: post.content || "" }} 
          />

          {}
          <div className="mt-12 flex flex-col gap-6 border-y border-border py-8 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap gap-2">
              {post.tags && post.tags.length > 0 ? (
                post.tags.map((tag) => (
                  <div key={tag.id} className="flex items-center gap-1 rounded-md bg-muted px-2.5 py-1 text-xs font-bold text-muted-foreground transition-colors hover:bg-primary/10 hover:text-primary">
                    <Tag className="h-3 w-3" />
                    {tag.name}
                  </div>
                ))
              ) : (
                <span className="text-sm text-muted-foreground">Industrial Tags</span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm font-bold text-foreground uppercase tracking-wider">Share:</span>
              <Button variant="outline" size="icon" className="h-8 w-8 rounded-full border-border hover:text-primary hover:border-primary/50">
                <Share2 className="h-4 w-4" />
              </Button>
              {}
            </div>
          </div>

          {}
          <div className="mt-12 rounded-md bg-muted/20 border border-border/40 p-8">
            <div className="flex flex-col gap-6 sm:flex-row sm:items-start">
              <Avatar className="h-20 w-20 border-2 border-background shadow-sm">
                <AvatarImage src={authorAvatar} alt={authorName} />
                <AvatarFallback className="text-lg bg-muted text-muted-foreground"><User className="h-8 w-8" /></AvatarFallback>
              </Avatar>
              <div className="flex-1 space-y-2 text-center sm:text-left">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                  <h3 className="text-xl font-bold text-foreground">About {authorName}</h3>
                  <Link to={post.author?.link || "#"} className="mt-2 text-sm font-bold text-primary hover:underline sm:mt-0 uppercase tracking-wide">
                    Expert Profile
                  </Link>
                </div>
                <p className="text-muted-foreground leading-relaxed">
                  {post.author?.description || `Expert CNC technician at CNCWise. Passionate about helping machine shops reduce downtime and optimize maintenance with AI-driven diagnostics.`}
                </p>
              </div>
            </div>
          </div>
        </div>

        {}
        <aside className="space-y-8">
          {}
          <Card className="bg-primary text-primary-foreground border-none shadow-lg">
            <CardHeader>
              <CardTitle className="text-xl font-bold tracking-tight">Optimize Your CNC Shop</CardTitle>
              <CardDescription className="text-primary-foreground/90 font-medium">
                Reduce downtime with AI diagnostics and preventative maintenance tracking.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary-foreground/20 text-[10px] font-bold">1</span>
                  AI Troubleshooting
                </li>
                <li className="flex items-center gap-2">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary-foreground/20 text-[10px] font-bold">2</span>
                  Maintenance Portal
                </li>
                <li className="flex items-center gap-2">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary-foreground/20 text-[10px] font-bold">3</span>
                  Component Repair Discounts
                </li>
              </ul>
            </CardContent>
            <CardFooter>
              <Link to="#" className="w-full">
                <Button variant="secondary" className="w-full font-bold uppercase tracking-widest text-primary shadow-sm hover:bg-white">
                  Start Your Subscription
                </Button>
              </Link>
            </CardFooter>
          </Card>

          {}
          <Card className="border-border/60 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg font-bold">Maintenance Updates</CardTitle>
              <CardDescription>
                Get the latest CNC repair tips and software updates.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <input 
                type="email" 
                placeholder="Shop email address" 
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 transition-all"
              />
              <Button className="w-full rounded-md font-bold uppercase tracking-wider">Subscribe</Button>
            </CardContent>
          </Card>
        </aside>
      </div>

      <Separator className="my-16 opacity-50" />

      {}
      <RelatedPostsSection />
    </article>
  );
}

export default function SinglePostTemplateSection() {
  
  const currentWPQueryParams = wvcClient.getCurrentWPQueryParams();
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section id="single-post-template-section" className="bg-background text-foreground min-h-screen">
      <PostsProvider wp_query={wp_query}>
        <SinglePostConsumer />
      </PostsProvider>
    </section>
  );
}